// Top-bar alerts bell with severity count badge. Click to open a
// flyout listing all live alerts (red → gold → grey). Click an alert
// to focus its entity on the map (and switch the workspace card).
//
// Lives in the ContextBar, not the map, so it's always visible and
// doesn't crowd the canvas.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ALERTS, SEVERITY_META } from './mockAlerts';
import { useIntel } from './IntelligenceContext';
import { C } from '../styles';

const SEVERITY_ORDER = { red: 0, gold: 1, grey: 2 };

function AlertsBell() {
  const intel = useIntel();
  const { setSelected, dismissedAlertIds, dismissAlert } = intel;
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const visible = useMemo(() => {
    return ALERTS
      .filter(a => !dismissedAlertIds.has(a.id))
      .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  }, [dismissedAlertIds]);

  const counts = useMemo(() => {
    const c = { red: 0, gold: 0, grey: 0 };
    visible.forEach(a => { c[a.severity]++; });
    return c;
  }, [visible]);

  const total = visible.length;
  const hasRed = counts.red > 0;

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Tooltip title="Alerts">
        <IconButton
          size="small"
          onClick={() => setOpen(o => !o)}
          sx={{
            color: hasRed ? '#FF0000' : (counts.gold > 0 ? C.accent : C.textSecondary),
            backgroundColor: open ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
          }}
        >
          <NotificationsIcon sx={{ fontSize: 20 }} />
          {total > 0 && (
            <Box sx={{
              position: 'absolute',
              top: 4, right: 4,
              minWidth: 14, height: 14,
              px: '3px',
              borderRadius: '7px',
              backgroundColor: hasRed ? '#FF0000' : '#F9B612',
              border: '1.5px solid #0d1019',
              color: hasRed ? '#fff' : '#000',
              fontSize: '0.55rem', fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1,
              ...(hasRed ? {
                animation: 'bellPulse 1.6s ease-in-out infinite',
                '@keyframes bellPulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%':      { transform: 'scale(1.15)' },
                },
              } : {}),
            }}>
              {total}
            </Box>
          )}
        </IconButton>
      </Tooltip>

      {open && (
        <Box sx={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          right: 0,
          width: 380,
          maxHeight: 520,
          zIndex: 30,
          backgroundColor: 'rgba(13, 16, 25, 0.97)',
          border: `1px solid ${C.border}`,
          borderRadius: 1,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          boxShadow: '0 12px 36px rgba(0, 0, 0, 0.6)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 1,
            borderBottom: `1px solid ${C.border}`,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          }}>
            <Typography sx={{ fontSize: '0.65rem', color: C.textMuted, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
              Alert Queue
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {['red', 'gold', 'grey'].map(s => counts[s] > 0 && (
                <Chip key={s} size="small" label={counts[s]}
                  sx={{
                    height: 18, fontSize: '0.55rem', fontWeight: 800,
                    color: SEVERITY_META[s].color,
                    backgroundColor: SEVERITY_META[s].bg,
                    border: `1px solid ${SEVERITY_META[s].border}`,
                    '& .MuiChip-label': { px: 0.6 },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* List */}
          <Box sx={{
            flex: 1, overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 3 },
          }}>
            {visible.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.75rem', color: C.textMuted, fontStyle: 'italic' }}>
                  No alerts.
                </Typography>
              </Box>
            ) : visible.map(a => (
              <AlertRow
                key={a.id}
                alert={a}
                onSelect={() => {
                  if (a.entity) setSelected(a.entity);
                  setOpen(false);
                }}
                onDismiss={() => dismissAlert(a.id)}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function AlertRow({ alert, onSelect, onDismiss }) {
  const meta = SEVERITY_META[alert.severity];
  return (
    <Box
      onClick={onSelect}
      sx={{
        cursor: alert.entity ? 'pointer' : 'default',
        px: 1.25, py: 1,
        borderBottom: `1px solid ${C.border}`,
        borderLeft: `3px solid ${meta.color}`,
        '&:hover': { backgroundColor: meta.bg },
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
        <Chip size="small" label={meta.label.toUpperCase()}
          sx={{
            height: 16, fontSize: '0.5rem', fontWeight: 800, letterSpacing: 0.5,
            color: meta.color, backgroundColor: meta.bg,
            border: `1px solid ${meta.border}`, borderRadius: 0.4,
            '& .MuiChip-label': { px: 0.6 },
          }}
        />
        {alert.isPredictive && (
          <Tooltip title="Predicted">
            <AutoAwesomeIcon sx={{ fontSize: 11, color: '#00BCD4' }} />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <AccessAlarmIcon sx={{ fontSize: 10, color: C.textMuted }} />
        <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontVariantNumeric: 'tabular-nums' }}>
          {alert.age}
        </Typography>
        <IconButton size="small"
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          sx={{ p: 0.2, color: C.textMuted, '&:hover': { color: '#f44336' } }}
        >
          <CloseIcon sx={{ fontSize: 11 }} />
        </IconButton>
      </Box>
      <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: C.textPrimary, lineHeight: 1.2 }}>
        {alert.title}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: C.textSecondary, lineHeight: 1.3, mt: 0.2 }}>
        {alert.body}
      </Typography>
      {alert.entity && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.3, color: C.textMuted }}>
          <Typography sx={{ fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {alert.entity.kind} · {alert.entity.id}
          </Typography>
          <ChevronRightIcon sx={{ fontSize: 12 }} />
        </Box>
      )}
    </Box>
  );
}

export default AlertsBell;
